import type { LandmarkName, TuroPose } from "./turo-world-poses";

/**
 * Turo World Level 3 — "The Right Words". Ten new poses.
 *
 * 🔴 L3 INTRODUCES NO NEW SAFETY CONCEPT, AND THAT IS LOAD-BEARING.
 * `TURO_WORLD_L2_L3_PLAN.md` §4.1: *"A child who never plays L3 is missing
 * nothing protective."* §4.2 explains why the split is drawn here rather than
 * anywhere else: *"Children play out of order, abandon halfway, and repeat
 * favourites. A child who plays only L2 must come away with a complete skill."*
 * So refusal AND telling both live in L2; L3 is courtesy language.
 *
 * ⚠️ GRADING RETURNS — but the plan's warning is inverted in this repo. §4.1
 * says *"make sure `practice` doesn't leak in as a default"*; here L1 was
 * ALREADY no-fail, so L3 is the level that needed new work to be gradeable.
 * Plan §9 decision 1 answers it: L3 runs on `curriculum/adaptive.ts` +
 * `mastery.ts`, the engine Letter Lagoon and Sky Quest already use, and
 * `engine/warmup` stays the pure no-fail ritual.
 *
 * 🔴 `morning_stretch` IS THE ONLY POSE IN THIS FILE THAT MAY WRITE
 * CALIBRATION — validator rules 22 and 23. It is functionally L1's `arms_up`:
 * both arms fully extended overhead puts the wrists further from the torso than
 * anything else in the bank with nothing occluded, which is exactly the frame a
 * body-scale measurement wants. Every other L3 pose is `low`.
 */

const TRUNK: LandmarkName[] = [
  "leftShoulder",
  "rightShoulder",
  "leftHip",
  "rightHip",
];
const ARMS: LandmarkName[] = [
  ...TRUNK,
  "leftElbow",
  "rightElbow",
  "leftWrist",
  "rightWrist",
];

const WAVE_BYE: TuroPose = {
  id: "wave_bye",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: ARMS,
  /**
   * 🔴 THE SAME TARGETS AS `wave_hello`, DELIBERATELY AND EXACTLY. L3 S1's core
   * idea (§4.3) is *"hello and goodbye are the SAME gesture — the words differ,
   * not the hands"*, and children find that genuinely surprising. Declaring a
   * different angle here would teach the opposite of the session.
   */
  targets: [
    { measure: "elbow", side: "left", value: 105 },
    { measure: "shoulderAbduction", side: "left", value: 120 },
  ],
  audio: { cue: "vo_turo_greet_wavebye_cue" },
  cue: { en: "Wave goodbye! Bye bye!" },
  sprite: "wave_bye",
  ghost: "wave_bye",
};

const FLYING_KISS: TuroPose = {
  id: "flying_kiss",
  frame: "upper",
  graded: true,
  /** A hold, per C-3 — L1 already shipped the plan's own motion fallback. */
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: [...ARMS, "nose"],
  targets: [
    /* Fingertips at the mouth — graded as "hand near the face". */
    { measure: "wristToNose", side: "left", value: 0.45, op: "lte" },
  ],
  audio: { cue: "vo_turo_greet_kiss_cue" },
  cue: { en: "Send a kiss! Mwah!" },
  sprite: "flying_kiss",
  ghost: "flying_kiss",
};

const THANK_YOU: TuroPose = {
  id: "thank_you",
  frame: "upper",
  /**
   * 🔴 UNGRADED, BECAUSE A CHILD STANDING STILL PASSES IT. Arms hanging at the
   * sides are wide apart, low on the torso and nearly straight — every target
   * below, within tolerance, with the hips in shot. Measured against the real
   * matcher when L3 started watching the child (`TwRound`): a child who did
   * nothing was told "Lovely. You did that with me." on the thank-you lesson.
   *
   * 📋 TO GRADE IT AGAIN, THE TARGETS NEED SOMETHING A RESTING ARM CANNOT DO —
   * e.g. the hands raised in front to waist height (`wristHeightInTorso` lte
   * ~0.8) rather than a straight elbow, which a forearm reaching toward the
   * camera does not even show. That is a content change, checked against the
   * matcher the same way, not a flag.
   */
  graded: false,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /*
     * 🔴 HANDS WIDE APART — THE SEPARATOR FROM `please_asking`. Plan §5.1 lists
     * them as a collision: both are hands-forward-palms-up. Asset pack §5 tells
     * the artist the same thing — thank you has hands "WELL APART", please has
     * them "TOGETHER and close in".
     *
     * ⚠️ `please_asking` IS UNGRADED (below), so this separation lives only in
     * the ART and the child's copy. It is declared anyway, because the day
     * `please_asking` becomes gradeable this target is what keeps them apart.
     */
    { measure: "wristSeparation", side: "left", value: 0.6, op: "gte" },
    { measure: "wristHeightInTorso", side: "left", value: 0.55, op: "gte" },
    { measure: "elbow", side: "left", value: 150 },
    { measure: "elbow", side: "right", value: 150 },
  ],
  audio: { cue: "vo_turo_greet_thankyou_cue" },
  cue: { en: "Open your hands. Thank you!" },
  sprite: "thank_you",
  ghost: "thank_you",
};

const SORRY: TuroPose = {
  id: "sorry",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: ARMS,
  targets: [
    /* One hand flat at the chest centre, the other down — plan §5. */
    { measure: "wristToOppositeShoulder", side: "left", value: 0.6, op: "lte" },
    { measure: "wristHeightInTorso", side: "left", value: 0.4, op: "lte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.9, op: "gte" },
  ],
  audio: { cue: "vo_turo_greet_sorry_cue" },
  cue: { en: "Hand on your chest. Sorry." },
  sprite: "sorry",
  ghost: "sorry",
};

const EXCUSE_ME: TuroPose = {
  id: "excuse_me",
  frame: "upper",
  /** Plan §5, "not checked" — one raised index finger is invisible to a pose model. */
  graded: false,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: "vo_turo_greet_excuseme_cue" },
  cue: { en: "One finger up. Excuse me!" },
  sprite: "excuse_me",
  ghost: "excuse_me",
};

const PLEASE_ASKING: TuroPose = {
  id: "please_asking",
  frame: "upper",
  /**
   * Plan §5, "not checked" — *"(voice-ritual beat)"*. The child SAYS please;
   * §6 keeps voice ungated, so the beat advances on the timer and Turo's warmth
   * is identical whether the mic heard anything or not.
   */
  graded: false,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: [],
  targets: [],
  audio: { cue: "vo_turo_greet_please_cue" },
  cue: { en: "Hands together. Please can I have it?" },
  sprite: "please_asking",
  ghost: "please_asking",
};

const MORNING_STRETCH: TuroPose = {
  id: "morning_stretch",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 3,
  /**
   * 🔴 THE ONLY POSE IN L3 THAT MAY WRITE `bodyScale` — rules 22 and 23, and
   * plan §4.4's "calibration bonus". Functionally L1's `arms_up`: both arms
   * fully extended overhead, wrists as far from the torso as the bank goes, and
   * nothing occluded. Pin it to S3's FIRST graded beat and let it write with
   * `confidence: high`.
   */
  calibrationQuality: "high",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    { measure: "elbow", side: "left", value: 170 },
    { measure: "elbow", side: "right", value: 170 },
    { measure: "wristHeightInTorso", side: "left", value: -0.5, op: "lte" },
    { measure: "wristHeightInTorso", side: "right", value: -0.5, op: "lte" },
  ],
  audio: { cue: "vo_turo_greet_morning_cue" },
  cue: { en: "Big stretch! Good morning!" },
  sprite: "morning_stretch",
  ghost: "morning_stretch",
};

const AFTERNOON_SHADE: TuroPose = {
  id: "afternoon_shade",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: [...ARMS, "nose"],
  targets: [
    /* A visor hand at the brow, the other down — plan §5. */
    { measure: "wristToNose", side: "left", value: 0.4, op: "lte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.9, op: "gte" },
  ],
  audio: { cue: "vo_turo_greet_afternoon_cue" },
  cue: { en: "Shade your eyes. Good afternoon!" },
  sprite: "afternoon_shade",
  ghost: "afternoon_shade",
};

const EVENING_DIYA: TuroPose = {
  id: "evening_diya",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: false,
  requiredLandmarks: ARMS,
  targets: [
    /*
     * 🔴 THE SEPARATOR FROM `namaste`, AND PLAN §5 CALLS IT THE FAILURE THAT
     * BREAKS THE SESSION: *"if it comes back with flat pressed palms at chest
     * height, it's a namaste and the whole session breaks."* Both are hands
     * together; the diya is held LOWER. The art run hit this exact failure once
     * and had to be rerolled — the detector must not repeat it.
     */
    { measure: "wristSeparation", side: "left", value: 0.25, op: "lte" },
    { measure: "wristHeightInTorso", side: "left", value: 0.5, op: "gte" },
    { measure: "wristHeightInTorso", side: "right", value: 0.5, op: "gte" },
  ],
  audio: { cue: "vo_turo_greet_evening_cue" },
  cue: { en: "Cup your hands, like a little light. Good evening!" },
  sprite: "evening_diya",
  ghost: "evening_diya",
};

const NIGHT_SLEEP: TuroPose = {
  id: "night_sleep",
  frame: "upper",
  graded: true,
  type: "hold",
  availableFrom: 3,
  calibrationQuality: "low",
  mirrorTolerant: true,
  requiredLandmarks: [...ARMS, "nose"],
  targets: [
    /* Palms together beside the head — plan §5's "<0.25T apart, above shoulder". */
    { measure: "wristSeparation", side: "left", value: 0.3, op: "lte" },
    { measure: "wristHeightInTorso", side: "left", value: 0.05, op: "lte" },
    { measure: "wristToNose", side: "left", value: 0.6, op: "lte" },
  ],
  audio: { cue: "vo_turo_greet_night_cue" },
  cue: { en: "Hands beside your cheek. Good night!" },
  sprite: "night_sleep",
  ghost: "night_sleep",
};

export const L3_POSES: readonly TuroPose[] = [
  WAVE_BYE, FLYING_KISS,
  THANK_YOU, SORRY, EXCUSE_ME, PLEASE_ASKING,
  MORNING_STRETCH, AFTERNOON_SHADE, EVENING_DIYA, NIGHT_SLEEP,
];

export const l3PoseById = (id: string): TuroPose | undefined =>
  L3_POSES.find((p) => p.id === id);
